import { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { taskPath } from '@/utils/taskPath'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import {
    subscribeProjectActivitySaga,
    unsubscribeProjectActivitySaga,
    loadMoreProjectActivitySaga,
    clearProjectActivity,
} from '@/store/activity/activitySlice'
import type { ActivityEvent } from '@/store/activity/activitySlice'
import {
    selectProjectActivity,
    selectProjectActivityHasMore,
    selectProjectActivityLoading,
} from '@/store/activity/activitySelectors'
import { selectTasksById } from '@/store/tasks/tasksSelectors'
import { setOpenModal } from '@/store/ui/uiSlice'
import Modal from '@/components/modals/Modal/Modal'
import Avatar from '@/components/ui/Avatar/Avatar'
import ActivitySourceBadge from '@/components/app/ActivitySourceBadge/ActivitySourceBadge'
import Button from '@/components/ui/Button/Button'
import IconClose from '@/assets/icons/icon-close.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import { TextH2, TextMidd, TextNorm } from '@/components/ui/Text'
import { useRelativeTime } from '@/hooks/useRelativeTime'
import { selectListById } from '@/store/lists/listsSelectors'
import { getActivityActionText, groupByDay } from '@/store/activity/activityUtils'
import { getActivityActorDisplay } from '@/utils/activityActorDisplay'
import css from './ProjectActivityModal.module.css'

interface Props {
    projectId: string
    onClose: () => void
}


const ActivityEventRow = ({ event }: { event: ActivityEvent }) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { getMember, statuses, workspace } = useWorkspace()
    const workspaceUrlId = workspace?.workspaceUrlId
    const tasksById = useAppSelector(selectTasksById)
    const fromList = useAppSelector(state =>
        event.meta.fromListId ? selectListById(state, event.meta.fromListId) : undefined
    )
    const toList = useAppSelector(state =>
        event.meta.toListId ? selectListById(state, event.meta.toListId) : undefined
    )
    const formatRelativeTime = useRelativeTime()

    const { displayName, avatarColor, avatarImage } = getActivityActorDisplay(event, getMember)

    const actionText = getActivityActionText(event, {
        statuses,
        getMember,
        fromListName: fromList?.name,
        toListName: toList?.name,
    })

    const task = event.taskId ? tasksById[event.taskId] : undefined
    const isProjectEvent = event.type === 'project_created' || event.type === 'project_deleted'
    const taskName = isProjectEvent
        ? (event.meta.projectName ?? null)
        : (task?.name ?? event.meta.taskName ?? null)

    const handleTaskClick = () => {
        if (!task || !workspaceUrlId || !workspace) return
        const path = taskPath({ workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: task.number, pending: task.pending })
        dispatch(setOpenModal(null))
        if (path) navigate(path, { state: { backgroundLocation: location } })
    }

    const canNavigate = !!task && !isProjectEvent

    return (
        <div className={css.event}>
            <div className={css.event_avatar}>
                <Avatar title={displayName} color={avatarColor} avatar={avatarImage} size="medium2" />
            </div>
            <div className={css.event_body}>
                {taskName && (
                    <span
                        className={canNavigate ? css.event_task : css.event_task_static}
                        onClick={canNavigate ? handleTaskClick : undefined}
                    >
                        <TextNorm>{taskName}</TextNorm>
                    </span>
                )}
                <div className={css.event_meta}>
                    <TextMidd className={css.event_action}>{displayName}<ActivitySourceBadge event={event} /> {actionText}</TextMidd>
                    {event.createdAt && <div className={css.dot} />}
                    {event.createdAt && (
                        <TextMidd className={css.event_time}>{formatRelativeTime(event.createdAt)}</TextMidd>
                    )}
                </div>
            </div>
        </div>
    )
}

const ProjectActivityModal = ({ projectId, onClose }: Props) => {
    const dispatch = useAppDispatch()
    const events = useAppSelector(state => selectProjectActivity(state, projectId))
    const hasMore = useAppSelector(state => selectProjectActivityHasMore(state, projectId))
    const loading = useAppSelector(state => selectProjectActivityLoading(state, projectId))

    useEffect(() => {
        dispatch(subscribeProjectActivitySaga(projectId))
        return () => {
            dispatch(unsubscribeProjectActivitySaga(projectId))
            dispatch(clearProjectActivity(projectId))
        }
    }, [projectId, dispatch])

    const groups = useMemo(() => groupByDay(events), [events])

    return (
        <Modal onClose={onClose} maxWidth={640}>
            <div className={css.close}>
                <IconButton icon={<IconClose />} onClick={onClose} />
            </div>
            <TextH2 className={css.title}>Activity</TextH2>
            {loading && events.length === 0 ? (
                <div className={css.empty}><TextNorm className={css.empty_text}>Loading…</TextNorm></div>
            ) : events.length === 0 ? (
                <div className={css.empty}><TextNorm className={css.empty_text}>No activity yet.</TextNorm></div>
            ) : (
                <>
                    <div className={css.groups}>
                        {groups.map(group => (
                            <div key={group.label}>
                                <TextH2 className={css.group_label}>{group.label}</TextH2>
                                <div className={css.group_items}>
                                    {group.events.map(event => (
                                        <ActivityEventRow key={event.id} event={event} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {hasMore && (
                        <div className={css.load_more}>
                            <Button
                                title="Load more"
                                type="light"
                                loading={loading}
                                onClick={() => dispatch(loadMoreProjectActivitySaga(projectId))}
                            />
                        </div>
                    )}
                </>
            )}
        </Modal>
    )
}

export default ProjectActivityModal
